import { createSelector, createSlice, current } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { v4 as uuidv4 } from 'uuid';
import SYSTEM, { SystemType } from '../constants/judgingSystems';
import type { SAjsCriterion } from '../logic/sajsCriteria';
import { SubSystemType } from '../constants/subSystemTypes';
import { SheetType } from '../constants/sheetStatus';
import TASK, { TaskType } from '../constants/taskStatus';
import SOCKET, { SocketType } from '../constants/socketTypes';
import { createAppAsyncThunk } from '../app/hooks';
import { StyleType } from '../constants/style';
import { AdjudicatorType } from '../constants/adjudicatorType';
import { WdsfApiParticipantStatusType } from '../constants/wdsfApiParticipantStatus';
import { HeatType } from '../constants/heatType';
import { BreakingJudgingLevelType } from '../constants/breaking';
import { WarningType } from '../constants/warningType';

export interface SocketElement {
  action: string;
  args: Array<any>;
  room?: string | null;
  callback?: string | null;
  namespace?: string | null;
  acknowledge: boolean;
  volatile: boolean;
  uuid?: string;
  socketId?: string;
}
export interface CurrentScreenSocket {
  adjudicator: string;
  taskUuid: string;
  category: string;
  round: string;
  dance: Dance;
  participant: TaskParticipant | null;
}
export interface CategoryShort {
  shortName: string;
  style: StyleType | string;
}
export interface Task {
  uuid: string;
  type: SocketType;
  roundUuid: string;
  adjudicatorUuid: string;
  ttItemUuid: string | null;
  judgingSystem: SystemType;
  participants: TaskParticipant[];
  dances: Dance[];
  heats: Heat[];
  sheet: SheetType;
  status: string;
  taskResults: TaskResults;
  round: RoundShort;
  category: CategoryShort;
  style?: string; // Style from ttItem
  joinedWith?: string[]; // UUIDs of other ttItems in the join group
  isJoinedTo?: string; // UUID of parent ttItem if this is a child
  assignedComponent?: string; // EL-AJS component assigned to this adjudicator
}

/**
 * Composite identity for a task-like object. A round split across multiple
 * ttItems (10-dance styles: e.g. Standard + Latin) shares one roundUuid, so
 * ttItemUuid is the real key; fall back to roundUuid for training tasks
 * (ttItemUuid null) and legacy persisted data.
 */
export const taskKey = (t: {
  ttItemUuid?: string | null;
  roundUuid: string;
}): string => t.ttItemUuid || t.roundUuid;
export interface TaskParticipant {
  uuid: string;
  entryUuid: string;
  startNo: number;
  /** Carried on the sheet so a judge can see who they are scoring. */
  name?: string;
  country?: string;
  customFlag?: string;
}
export interface ParticipantResult {
  uuid: string;
  roundUuid: string;
  danceUuid: string;
  judgingSystem: SystemType;
  result: any;
}
export interface ParticipantWarning {
  participantUuid: string;
  roundUuid: string;
  danceUuid: string;
  adjudicatorUuid: string;
  warning: string;
  type: WarningType;
  uuid: string;
}
export interface TaskResults {
  uuid: string;
  roundUuid: string;
  ttItemUuid: string | null;
  judgingSystem: SystemType;
  participantResults: ParticipantResult[];
  participantWarnings?: ParticipantWarning[];
  status: TaskType;
  adjudicatorUuid: string;
}
export interface ManagerItem {
  uuid: string;
  competitionUuid: string;
  roundUuid: string;
  active: boolean;
  nextActive: boolean;
  index: number;
  paused: boolean;
  name: string;
  ttItemUuid?: string | null;
  hall?: number; // Hall number this task belongs to
}
// export interface RoundParticipant {
//   uuid: string;
//   entryUuid: string;
//   roundUuid: string;
//   startNo: number;
//   started: boolean;
//   starsLeft: number;
//   starParticipant: boolean;
//   notStartedReason: null | 'excused' | 'no_show';
//   notFinished: boolean;
//   notFinishedReason: null | 'disqualified' | 'resigned' | 'health';
//   name: string;
// }
export interface RoundParticipant {
  uuid: string;
  entryUuid: string;
  roundUuid: string;
  startNo: number;
  started: boolean;
  starsLeft: number;
  starParticipant: boolean;
  notStartedReason: null | WdsfApiParticipantStatusType;
  notFinished: boolean;
  notFinishedReason: null | 'disqualified' | 'resigned' | 'health';
  finishedInRound: boolean;
  finishedPlace: number | null;
  finalRank?: number | null;
  name: string;
  details: EntryDetailsType;
  /** Single dance: dance uuids this couple dances in the round. */
  selectedDanceUuids?: string[];
  /** Single dance, eliminating round: danceUuid -> called back (true) / out (false). */
  danceRecalls?: Record<string, boolean>;
  /** Single dance: danceUuid -> shared place, set when out of that dance. */
  dancePlaces?: Record<string, number>;
  rls?: string | null;
  teamId?: string | null;
  personId?: string | null;
  wdsfApiId?: number | null;
}
export interface EntryDetailsType {
  city: string;
  club: string;
  country: string;
  coupleAge: string;
  entryName: string;
  man: string;
  woman: string;
  wdsf?: EntryDetailsWdsfType;
  pzst?: EntryDetailsPzstType;
  person1?: EntryDetailsPersonType;
  person2?: EntryDetailsPersonType;
  athletes?: EntryDetailsAthletesType[];
  additionalInfo?: string; // Additional info from PZST API (class change, etc.) or manually entered
  idNMB?: string; // optional National Member Body ID
  customFlag?: string; // optional custom flag designator (e.g. "ain-by") that overrides country-based flag image
}
export interface EntryDetailsPzstType {
  pzstId: string | null;
}
export interface EntryDetailsWdsfType {
  rls: string;
  teamId: string;
}
export interface EntryDetailsPersonType {
  firstName: string;
  lastName: string;
  min: string;
  birthYear: string;
  // Stage name (b-boy / b-girl "ksywa"). Shown instead of firstName/lastName on
  // breaking screens and results; falls back to "Firstname SURNAME" when absent.
  // The registration webservices send `null` when they have none recorded.
  nickname?: string | null;
}
export interface EntryDetailsAthletesType {
  name: string;
  surname: string;
  yearOfBirth: string;
  id: string;
}
export interface RoundParticipantShort {
  uuid: string;
  entryUuid: string;
  roundUuid: string;
}
export interface HeatParticipant {
  uuid: string;
  entryUuid: string;
  roundUuid: string;
  startNo: number;
  name?: string;
}
export interface Round {
  categoryUuid: string;
  dances: Dance[];
  expectedNumber: number;
  groupDanceTime: number;
  heatType: HeatType;
  heatsNumber: number;
  heats: Heat[];
  maxInHeat: number;
  index: number;
  isFinal: boolean;
  isPreliminary: boolean;
  isRedance: boolean;
  judgingSystem: SystemType;
  markedCouples: number;
  number: number;
  redance: boolean;
  roundName: string;
  soloDanceTime: number;
  stars: number;
  state: string;
  uuid: string;
  roundParticipants: Array<RoundParticipant>;
  judgedCriteria: number;
  status?: {
    resultsConfirmed: boolean;
    resultsPrinted: boolean;
    diplomasPrinted: boolean;
    roundClosed: boolean;
  };
}
export interface RoundShort {
  dances: Dance[];
  heats: number;
  isFinal: boolean;
  judgingSystem: SystemType;
  systemSubtype?: SubSystemType;
  markedCouples: number;
  roundName: string;
  judgedCriteria: number;
  minScoreGuidance?: number;
  maxScoreGuidance?: number;
  sajsCriteria?: SAjsCriterion[];
  // Breaking: the judging interface the panel scores on - it decides the judge's
  // slider set. Absent on sheets from a desktop that predates the setting, which
  // the battle screen reads as Level A.
  breakingJudgingLevel?: BreakingJudgingLevelType;
  // Breaking: the battle the operator has activated. The phone follows it, so
  // the whole panel always scores the same battle.
  currentBattle?: BreakingCurrentBattle;
  // Breaking: the battle queued behind the active one, if the round still has
  // one to score. When the scrutineer confirms the battle in hand the phone
  // carries straight on to this one instead of returning home to wait for the
  // operator to activate it.
  nextBattle?: BreakingCurrentBattle;
}

/** The battle currently on the floor, pushed with the adjudicator sheet. */
export interface BreakingCurrentBattle {
  uuid: string;
  bracketSlot: number;
  /** Battle rounds to score, including any tie-break added on the night. */
  battleRounds: number;
  redName: string;
  blueName: string;
  redEntryUuid: string | null;
  blueEntryUuid: string | null;
  /** Country and flag per side, so the judge's screen shows who is who. */
  redCountry?: string;
  blueCountry?: string;
  redCustomFlag?: string;
  blueCustomFlag?: string;
  /**
   * Set once the scrutineer confirms the battle. The phone waits after the last
   * battle round is sent; a winner here means the battle is closed and the phone
   * returns home.
   */
  winnerEntryUuid?: string | null;
}
export interface Heat {
  uuid: string;
  roundUuid: string;
  danceUuid: string;
  participants: HeatParticipant[];
  solo: boolean;
  heatNo: number;
  danceHeatNo: number;
}
export interface Dance {
  desig: string;
  name: string;
  index: number;
  roundIndex: number;
  solo: boolean;
  style: StyleType;
  uuid: string;
  skipped?: boolean;
}
export interface JudgesListSettings {
  pinLoginEnabled?: boolean;
}
export interface JudgesListPayload {
  list: object;
  settings: JudgesListSettings;
}
/**
 * One official on the list marX broadcasts (`judgesList`). Built in one place
 * on the desktop: redux/components/judges/judgesListThunks.ts.
 */
export interface JudgesListEntry {
  uuid: string;
  letter: string | null;
  name: string;
  country?: string;
  pinCode?: string | null;
  /**
   * Which list the official is on. It keeps the two logins apart: a judge
   * picks themselves or types a PIN, a chairperson always types theirs, and
   * neither may be signed in by the other's PIN.
   */
  role?: 'adjudicator' | 'chairperson';
}
/**
 * The chairperson signed in on this device. The PIN they typed is kept with
 * them so that changing it on the desktop signs the device out - see
 * updateJudgesList.
 */
export interface ChairpersonSession {
  uuid: string;
  name: string;
  pinCode: string;
}

/** The stored list as an array: marX sends one, older states hold an object. */
export const toJudgesListEntries = (list: unknown): JudgesListEntry[] => {
  if (!list) return [];
  if (Array.isArray(list)) return list as JudgesListEntry[];
  if (typeof list === 'object') return Object.values(list) as JudgesListEntry[];
  return [];
};
export interface WorkState {
  timetable: object;
  judgeLetter: string;
  judgeName: string;
  judgeUuid: string;
  currentTask: string | null;
  nextTask: string | null;
  taskManagerQueue: Array<ManagerItem>;
  tasks: Array<Task>;
  tasksCompleted: Array<Task>;
  tasksResults: Array<TaskResults>;
  participantResults: any;
  judgesList: object;
  judgesListSettings: JudgesListSettings;
  /**
   * Who is using the chairperson screen, or nobody. Optional because a state
   * persisted by an earlier build has no such field; that reads as nobody.
   */
  chairperson?: ChairpersonSession | null;
  socketsCache: Array<SocketElement>;
  lastCompletedTtUuid: string | null;
  /**
   * Breaking pre-selection: athletes whose score the judge has locked, keyed by
   * task uuid, so a finger scrolling past a fader cannot move it. Persisted with
   * the rest of the work slice, so a reload keeps the locks until the sheet is
   * sent. Optional: state persisted before it existed has none.
   */
  scoreLocks?: Record<string, string[]>;
}
export interface Adjudicator {
  id: string | number;
  min: string | number;
  firstName: string;
  lastName: string;
  country: string;
  city: string;
  type: AdjudicatorType;
  subtype: any;
  classes: any;
  letter: string;
  idTs: number;
  uuid: string;
  wdsfApiId?: number | null;
}
const initialState: WorkState = {
  timetable: {},
  judgeLetter: '',
  judgeName: '',
  judgeUuid: '',
  currentTask: '',
  nextTask: '',
  tasks: [],
  tasksCompleted: [],
  tasksResults: [],
  participantResults: [],
  judgesList: {},
  judgesListSettings: { pinLoginEnabled: false },
  chairperson: null,
  socketsCache: [],
  taskManagerQueue: [],
  lastCompletedTtUuid: null,
  scoreLocks: {}
};

export interface ServerToClientEvents {
  noArg: () => void;
  basicEmit: (a: number, b: string, c: Buffer) => void;
  withAck: (d: string, callback: (e: number) => void) => void;
}

export interface ClientToServerEvents {
  adjudicatorRoundResults: (arg0: TaskResults) => void;
}

export interface InterServerEvents {
  ping: () => void;
}

export interface SocketData {
  name: string;
  age: number;
}

const workSlice = createSlice({
  name: 'work',
  initialState,
  reducers: {
    updateTimetable(state, action: PayloadAction<object>) {
      state.timetable = action.payload;
    },

    updateJudgeLetter(state, action: PayloadAction<string>) {
      state.judgeLetter = action.payload;
    },
    updateJudgeName(state, action: PayloadAction<string>) {
      state.judgeName = action.payload;
    },
    updateJudgeUuid(state, action: PayloadAction<string>) {
      state.judgeUuid = action.payload;
    },
    updateJudgesList(state, action: PayloadAction<JudgesListPayload>) {
      state.judgesList = action.payload.list;
      if (action.payload.settings) {
        state.judgesListSettings = action.payload.settings;
      }
      // A chairperson stays signed in only while the list still vouches for
      // them. Removed from it, or given another PIN on the desktop, and this
      // device goes back to the PIN screen - which makes changing a PIN the
      // way to take a tablet back from whoever holds it.
      if (state.chairperson) {
        const signedIn = state.chairperson;
        // Among the chairpeople only: somebody who also judges is on the list
        // twice under one uuid, and the judge's entry comes first.
        const entry = toJudgesListEntries(action.payload.list).find(
          (official) =>
            official?.role === 'chairperson' && official.uuid === signedIn.uuid
        );
        if (!entry || entry.pinCode !== signedIn.pinCode) {
          state.chairperson = null;
        }
      }
    },
    signInChairperson(state, action: PayloadAction<ChairpersonSession>) {
      state.chairperson = action.payload;
    },
    signOutChairperson(state) {
      state.chairperson = null;
    },
    updateTaskManagerQueue(state, action: PayloadAction<ManagerItem[]>) {
      state.taskManagerQueue = action.payload;
      // currentTask and nextTask are derived by selectors using hall filtering
      // The reducer cannot access settings.selectedHall, so we don't set them here
    },
    updateCurrentTask(
      state,
      action: PayloadAction<ManagerItem | string | null>
    ) {
      if (action.payload === null) {
        state.currentTask = '';
      } else if (typeof action.payload === 'string') {
        state.currentTask = action.payload;
      } else {
        state.currentTask = taskKey(action.payload);
      }
    },
    updateNextTask(state, action: PayloadAction<ManagerItem | string | null>) {
      if (action.payload === null) {
        state.nextTask = '';
      } else if (typeof action.payload === 'string') {
        state.nextTask = action.payload;
      } else {
        state.nextTask = taskKey(action.payload);
      }
    },
    updateTask(state, action: PayloadAction<Task>) {
      const task = action.payload;
      const index = state.tasks.findIndex(
        (t: Task) =>
          taskKey(t) === taskKey(task) &&
          t.adjudicatorUuid === task.adjudicatorUuid
      );

      if (index === -1) {
        // If no matching task is found, add the new task
        state.tasks.push(task);
      } else {
        // If a matching task is found, update the existing task
        state.tasks[index] = task;
      }
    },

    updateTaskResults(state, action: PayloadAction<TaskResults>) {
      const taskResults = action.payload;
      const index = state.tasksResults.findIndex(
        (result: TaskResults) =>
          taskKey(result) === taskKey(taskResults) &&
          result.adjudicatorUuid === taskResults.adjudicatorUuid
      );

      if (index === -1) {
        // If no matching result is found, add the new task results
        state.tasksResults.push(taskResults);
      } else {
        // If a matching result is found, update the existing task results
        state.tasksResults[index] = taskResults;
      }
    },
    updateParticipantResults(
      state,
      action: PayloadAction<ParticipantResult[]>
    ) {
      const currentAdjudicator = state.judgeUuid;

      if (typeof state.participantResults === 'undefined') {
        state.participantResults = [];
      }
      if (typeof state.participantResults[currentAdjudicator] === 'undefined') {
        state.participantResults = {
          ...state.participantResults,
          [currentAdjudicator]: []
        };
      }
      action.payload.forEach((newPR) => {
        const search = state.participantResults[currentAdjudicator].find(
          (_: ParticipantResult) =>
            _.danceUuid === newPR.danceUuid &&
            _.uuid === newPR.uuid &&
            _.roundUuid === newPR.roundUuid
        );

        if (typeof search === 'undefined') {
          state.participantResults[currentAdjudicator] =
            state.participantResults[currentAdjudicator].concat(newPR);
        } else {
          state.participantResults[currentAdjudicator] =
            state.participantResults[currentAdjudicator]
              // .filter((_: Task) => _.adjudicatorUuid !== task.adjudicatorUuid)
              .filter(
                (_: ParticipantResult) =>
                  !(
                    _.danceUuid === search.danceUuid &&
                    _.uuid === search.uuid &&
                    _.roundUuid === search.roundUuid
                  )
              )
              .concat(newPR);
        }
      });
    },
    clearParticipantResultsForRound(state, action: PayloadAction<Task>) {
      const currentAdjudicator = state.judgeUuid;
      const { dances } = action.payload;
      if (typeof state.participantResults === 'undefined') {
        state.participantResults = [];
      }
      if (typeof state.participantResults[currentAdjudicator] === 'undefined') {
        state.participantResults = {
          ...state.participantResults,
          [currentAdjudicator]: []
        };
      }
      let tmp = state.participantResults[currentAdjudicator];
      dances.forEach((d) => {
        tmp = tmp.filter(
          (_: ParticipantResult) =>
            !(
              _.roundUuid === action.payload.roundUuid && _.danceUuid === d.uuid
            )
        );
      });
      state.participantResults[currentAdjudicator] = tmp;

    },
    clearData(state) {
      // console.log(current(state));
      state.timetable = {};
      state.judgeLetter = '';
      state.judgeName = '';
      state.judgeUuid = '';
      state.currentTask = '';
      state.nextTask = '';
      state.tasks = [];
      state.tasksCompleted = [];
      state.tasksResults = [];
      state.taskManagerQueue = [];
      state.participantResults = [];
      state.judgesList = {};
      state.chairperson = null;
      state.socketsCache = [];
      state.taskManagerQueue = [];
      state.lastCompletedTtUuid = null;
      state.scoreLocks = {};
    },
    /** Locks or unlocks one athlete's pre-selection score on a sheet. */
    toggleScoreLock(
      state,
      action: PayloadAction<{ taskUuid: string; entryUuid: string }>
    ) {
      const { taskUuid, entryUuid } = action.payload;
      const locks = state.scoreLocks ?? {};
      const current = locks[taskUuid] ?? [];
      locks[taskUuid] = current.includes(entryUuid)
        ? current.filter((uuid) => uuid !== entryUuid)
        : [...current, entryUuid];
      if (locks[taskUuid].length === 0) delete locks[taskUuid];
      state.scoreLocks = locks;
    },
    /** Drops a sheet's locks once it has been handed in. */
    clearScoreLocks(state, action: PayloadAction<string>) {
      if (state.scoreLocks) delete state.scoreLocks[action.payload];
    },
    removeTask(state, action: PayloadAction<Task>) {
      const task = action.payload;
      const search = state.tasks.find(
        (_: Task) =>
          taskKey(_) === taskKey(task) &&
          _.adjudicatorUuid === task.adjudicatorUuid
      );
      if (search !== undefined) {
        state.tasks = state.tasks.filter(
          (_) =>
            !(
              taskKey(_) === taskKey(search) &&
              _.adjudicatorUuid === task.adjudicatorUuid
            )
        );
      }
    },
    updateTaskCompleted(state, action: PayloadAction<Task>) {
      const task = action.payload;
      const search = state.tasksCompleted.find(
        (_: Task) =>
          taskKey(_) === taskKey(task) &&
          _.adjudicatorUuid === task.adjudicatorUuid
      );
      if (typeof search === 'undefined') {
        state.tasksCompleted = state.tasksCompleted.concat(task);
      } else {
        state.tasksCompleted = state.tasksCompleted
          .filter(
            (_: Task) =>
              !(
                taskKey(_) === taskKey(search) &&
                _.adjudicatorUuid === task.adjudicatorUuid
              )
          )
          .concat(task);
      }
    },
    removeTaskCompleted(state, action: PayloadAction<Task>) {
      const task = action.payload;

      const newTasksCompleted = state.tasksCompleted.filter(
        (_) =>
          taskKey(_) !== taskKey(task) ||
          _.adjudicatorUuid !== task.adjudicatorUuid
      );
      state.tasksCompleted = newTasksCompleted;
    },
    removeTaskCompletedByRound(
      state,
      action: PayloadAction<{
        roundUuid: string;
        adjudicatorUuid: string;
        ttItemUuid?: string | null;
      }>
    ) {
      const { roundUuid, adjudicatorUuid, ttItemUuid } = action.payload;
      state.tasksCompleted = state.tasksCompleted.filter(
        (_) =>
          _.roundUuid !== roundUuid ||
          _.adjudicatorUuid !== adjudicatorUuid ||
          // When a ttItemUuid is provided, only clear the completion record of
          // that ttItem — a split round's other style must stay completed.
          (ttItemUuid != null && _.ttItemUuid != null && _.ttItemUuid !== ttItemUuid)
      );
    },
    /**
     * Queue a socket element that could not be sent, for replay on the next
     * connect. Adjudicator login/logout is deliberately NOT queued: it is a
     * snapshot of who was signed in at the time, and this cache is persisted,
     * so a stale entry would announce a previous judge on the next connect -
     * which makes the desktop treat it as a judge swap on that device and
     * revert their acknowledged sheets. SocketContext re-emits the *current*
     * login from `chosenJudge` on every connect, so nothing is lost by
     * dropping these.
     */
    addSocketToCache(state, action: PayloadAction<SocketElement>) {
      if (action.payload.action === SOCKET.ADJUDICATOR) return;
      const newSocket = Object.assign({}, action.payload, {
        uuid: uuidv4()
      });
      state.socketsCache.push(newSocket);
    },
    removeSocketFromCache(state, action: PayloadAction<SocketElement>) {
      state.socketsCache = state.socketsCache.filter(
        (socket) => socket?.uuid !== action.payload.uuid
      );
    },
    /**
     * Emptied once the queue has been replayed onto a live socket - without
     * this the cache only ever grows, and every past element is re-sent on
     * every reconnect for the life of the install.
     */
    clearSocketsCache(state) {
      state.socketsCache = [];
    },
    addParticipantWarning(state, action: PayloadAction<ParticipantWarning>) {
      const warning = action.payload;
      // A split round has one tasksResults record per ttItem sharing the
      // roundUuid; use the warning's dance to pick the task that covers it,
      // falling back to the plain round match.
      const taskForDance = state.tasks.find(
        (t: Task) =>
          t.roundUuid === warning.roundUuid &&
          t.adjudicatorUuid === warning.adjudicatorUuid &&
          t.dances.some((d) => d.uuid === warning.danceUuid)
      );
      const currentTaskResults = state.tasksResults.find(
        (result: TaskResults) =>
          (taskForDance
            ? taskKey(result) === taskKey(taskForDance)
            : result.roundUuid === warning.roundUuid) &&
          result.adjudicatorUuid === warning.adjudicatorUuid
      );

      if (currentTaskResults) {
        if (!currentTaskResults.participantWarnings) {
          currentTaskResults.participantWarnings = [];
        }
        currentTaskResults.participantWarnings.push(warning);
      }
    },
    removeParticipantWarning(
      state, 
      action: PayloadAction<{ roundUuid: string; danceUuid: string; adjudicatorUuid: string; type: WarningType }>
    ) {
      const { roundUuid, danceUuid, adjudicatorUuid, type } = action.payload;
      const taskForDance = state.tasks.find(
        (t: Task) =>
          t.roundUuid === roundUuid &&
          t.adjudicatorUuid === adjudicatorUuid &&
          t.dances.some((d) => d.uuid === danceUuid)
      );
      const currentTaskResults = state.tasksResults.find(
        (result: TaskResults) =>
          (taskForDance
            ? taskKey(result) === taskKey(taskForDance)
            : result.roundUuid === roundUuid) &&
          result.adjudicatorUuid === adjudicatorUuid
      );

      if (currentTaskResults?.participantWarnings) {
        currentTaskResults.participantWarnings = currentTaskResults.participantWarnings.filter(
          (warning) => !(
            warning.roundUuid === roundUuid && 
            warning.danceUuid === danceUuid && 
            warning.type === type
          )
        );
      }
    },
    updateLastCompletedTtUuid(state, action: PayloadAction<string | null>) {
      state.lastCompletedTtUuid = action.payload;
    }
  }
});

export const addEmptyTaskResults = createAppAsyncThunk(
  'work/addRoundTasksFromRound',
  async function (data: null, thunkApi) {
    const { getState, dispatch } = thunkApi;
    const currentTaskUuid = getState().work.currentTask;
    const adjudicatorUuid = getState().work.judgeUuid;
    // Composite-key pointer with legacy roundUuid fallback (see selectCurrentTask)
    const currentTask =
      getState().work.tasks.find(
        (_: Task) =>
          taskKey(_) === currentTaskUuid &&
          _.adjudicatorUuid === adjudicatorUuid
      ) ||
      getState().work.tasks.find(
        (_: Task) =>
          _.roundUuid === currentTaskUuid &&
          _.adjudicatorUuid === adjudicatorUuid
      );
    if (currentTaskUuid !== null && currentTask !== undefined) {
      // console.log(currentTaskUuid);
      // console.log(getState().work.tasks);
      const participantResults: ParticipantResult[] = [];
      currentTask?.dances.forEach((dance) => {
        currentTask.participants.forEach((participant) => {
          const newResult =
            currentTask.judgingSystem === SYSTEM.MARKS ? 0 : null;
          const newParticipantResult: ParticipantResult = {
            uuid: participant.uuid,
            roundUuid: currentTask.roundUuid,
            danceUuid: dance.uuid,
            result: newResult,
            judgingSystem: currentTask.judgingSystem
          };
          participantResults.push(newParticipantResult);
        });
      });
      const newTaskResults: TaskResults = {
        uuid: uuidv4(),
        roundUuid: currentTask.roundUuid,
        ttItemUuid: currentTask.ttItemUuid,
        judgingSystem: currentTask.judgingSystem,
        participantResults: [],
        status: TASK.IN_PROGRESS,
        adjudicatorUuid: adjudicatorUuid
      };
      dispatch(updateTaskResults(newTaskResults));
      dispatch(updateParticipantResults(participantResults));
    }
  }
);
export const updateParticipantResultsAndPrepareCurrent = createAppAsyncThunk(
  'work/updateParticipantResultsAndPrepareCurrent',
  async function (newParticipantResult: ParticipantResult, thunkApi) {
    const { dispatch } = thunkApi;
    // await Promise.all([
    //   dispatch(updateParticipantResults([newParticipantResult]))
    // ]).then(() => prepareCurrentRoundResults(TASK.IN_PROGRESS));
    dispatch(updateParticipantResults([newParticipantResult]));
    // prepareCurrentRoundResults(TASK.IN_PROGRESS);
  }
);
export const prepareCurrentRoundResults = createAppAsyncThunk(
  'work/prepareCurrentRoundResults',
  async function (status: TaskType, thunkApi) {
    const { getState, dispatch } = thunkApi;

    let newStatus: TaskType = TASK.IN_PROGRESS;
    if (status !== null) {
      newStatus = status;
    }
    const currentTaskUuid = getState().work.currentTask;
    const adjudicatorUuid = getState().work.judgeUuid;

    // The pointer is a composite key (ttItemUuid || roundUuid); keep the plain
    // roundUuid match as fallback for legacy persisted pointers.
    const currentTask =
      getState().work.tasks.find(
        (_: Task) =>
          taskKey(_) === currentTaskUuid &&
          _.adjudicatorUuid === adjudicatorUuid
      ) ||
      getState().work.tasks.find(
        (_: Task) =>
          _.roundUuid === currentTaskUuid &&
          _.adjudicatorUuid === adjudicatorUuid
      );

    // console.log('foundTask', currentTask);
    if (currentTask !== undefined) {
      const currentFetchedResult = getState().work.tasksResults.find(
        (_) =>
          taskKey(_) === taskKey(currentTask) &&
          _.adjudicatorUuid === adjudicatorUuid
      );
      let currentResult = currentFetchedResult;
      if (currentFetchedResult === undefined) {
        const newTaskResults: TaskResults = {
          uuid: uuidv4(),
          roundUuid: currentTask.roundUuid,
          ttItemUuid: currentTask.ttItemUuid,
          judgingSystem: currentTask.judgingSystem,
          participantResults: [],
          status: newStatus,
          adjudicatorUuid: adjudicatorUuid
        };
        currentResult = newTaskResults;
      }
      if (currentTaskUuid !== null && currentResult !== undefined) {
        // Marks are stored round-wide; restrict to this task's own dances so a
        // split round doesn't emit the other style's marks under this ttItem.
        const taskDanceUuids = new Set(
          (currentTask.dances || []).map((d) => d.uuid)
        );
        const participantResults = getState().work.participantResults[
          adjudicatorUuid
        ].filter(
          (_: ParticipantResult) =>
            _.roundUuid === currentTask.roundUuid &&
            (taskDanceUuids.size === 0 || taskDanceUuids.has(_.danceUuid))
        );
        const newResults = Object.assign({}, currentResult, {
          participantResults: participantResults
        });
        // console.log('pr', participantResults);
        // console.log('nr', newResults.participantResults);

        dispatch(updateTaskResults(newResults));
        const newTask: Task = Object.assign({}, currentTask, {
          status: newStatus,
          taskResults: newResults
        });
        if (newStatus === TASK.DONE) {
          const taskManagerQueue = getState().work.taskManagerQueue;

          const index = taskManagerQueue.findIndex(
            (_) => taskKey(_) === taskKey(newTask)
          );
          
          // Save the ttUuid of the completed task
          if (newTask.ttItemUuid) {
            dispatch(updateLastCompletedTtUuid(newTask.ttItemUuid));
          }
          
          if (taskManagerQueue[index + 1] !== undefined) {
            dispatch(updateCurrentTask(taskManagerQueue[index + 1]));
          } else {
            dispatch(updateCurrentTask(null));
          }

          dispatch(updateTask(newTask));
          dispatch(updateTaskCompleted(newTask));
          dispatch(removeTask(newTask));
        } else {
          dispatch(updateTask(newTask));
        }
      }
    }
  }
);

/**
 * Prepare results for all joined tasks
 */
export const prepareJoinedTasksResults = createAppAsyncThunk(
  'work/prepareJoinedTasksResults',
  async function (status: TaskType, thunkApi) {
    const { getState, dispatch } = thunkApi;

    const joinedTasks = selectJoinedTasks(getState());
    const adjudicatorUuid = getState().work.judgeUuid;
    
    if (!joinedTasks || joinedTasks.length === 0 || !adjudicatorUuid) {
      return;
    }

    let newStatus: TaskType = TASK.IN_PROGRESS;
    if (status !== null) {
      newStatus = status;
    }

    // Process each joined task
    for (const task of joinedTasks) {
      const existingResult = getState().work.tasksResults.find(
        (_) =>
          taskKey(_) === taskKey(task) &&
          _.adjudicatorUuid === adjudicatorUuid
      );

      const currentResult: TaskResults = existingResult || {
        uuid: uuidv4(),
        roundUuid: task.roundUuid,
        ttItemUuid: task.ttItemUuid,
        judgingSystem: task.judgingSystem,
        participantResults: [],
        status: newStatus,
        adjudicatorUuid: adjudicatorUuid
      };

      // Get participant results for this specific task's round, restricted to
      // the task's own dances (a join group can contain two ttItems of the
      // same split round, which share roundUuid but cover different dances).
      const joinedTaskDanceUuids = new Set(
        (task.dances || []).map((d) => d.uuid)
      );
      const participantResults = getState().work.participantResults[
        adjudicatorUuid
      ]?.filter(
        (_: ParticipantResult) =>
          _.roundUuid === task.roundUuid &&
          (joinedTaskDanceUuids.size === 0 ||
            joinedTaskDanceUuids.has(_.danceUuid))
      ) || [];

      const newResults: TaskResults = {
        uuid: currentResult.uuid,
        roundUuid: currentResult.roundUuid,
        ttItemUuid: currentResult.ttItemUuid,
        judgingSystem: currentResult.judgingSystem,
        participantResults: participantResults,
        participantWarnings: currentResult.participantWarnings,
        status: newStatus,
        adjudicatorUuid: currentResult.adjudicatorUuid
      };

      dispatch(updateTaskResults(newResults));
      
      const updatedTask: Task = {
        ...task,
        status: newStatus,
        taskResults: newResults
      };

      if (newStatus === TASK.DONE) {
        if (task.ttItemUuid) {
          dispatch(updateLastCompletedTtUuid(task.ttItemUuid));
        }
        dispatch(updateTask(updatedTask));
        dispatch(updateTaskCompleted(updatedTask));
        dispatch(removeTask(updatedTask));
      } else {
        dispatch(updateTask(updatedTask));
      }
    }
    
    // Handle navigation for DONE status
    if (newStatus === TASK.DONE) {
      const taskManagerQueue = getState().work.taskManagerQueue;
      const currentTask = selectCurrentTask(getState());
      
      if (currentTask) {
        const index = taskManagerQueue.findIndex(
          (_) => taskKey(_) === taskKey(currentTask)
        );
        
        if (taskManagerQueue[index + 1] !== undefined) {
          dispatch(updateCurrentTask(taskManagerQueue[index + 1]));
        } else {
          dispatch(updateCurrentTask(null));
        }
      }
    }
  }
);

// Export actions
export const {
  updateTimetable,
  updateJudgeLetter,
  updateJudgeName,
  updateJudgesList,
  signInChairperson,
  signOutChairperson,
  updateCurrentTask,
  updateNextTask,
  updateJudgeUuid,
  updateTaskManagerQueue,
  updateTask,
  updateTaskResults,
  updateParticipantResults,
  clearParticipantResultsForRound,
  removeTask,
  clearData,
  updateTaskCompleted,
  removeTaskCompleted,
  removeTaskCompletedByRound,
  addSocketToCache,
  removeSocketFromCache,
  clearSocketsCache,
  addParticipantWarning,
  removeParticipantWarning,
  updateLastCompletedTtUuid,
  toggleScoreLock,
  clearScoreLocks,
} = workSlice.actions;

/**
 * Start a local training session: wipe existing work state, install a synthetic
 * adjudicator, push the synthesized training task and select it as current.
 * No socket traffic is produced — see `selectIsTrainingMode` gating in screens.
 */
export const startTrainingSession = createAppAsyncThunk(
  'work/startTrainingSession',
  async function (
    payload: { task: Task; judge: { uuid: string; letter: string; name: string } },
    thunkApi
  ) {
    const { dispatch } = thunkApi;
    dispatch(clearData());
    dispatch(updateJudgeLetter(payload.judge.letter));
    dispatch(updateJudgeName(payload.judge.name));
    dispatch(updateJudgeUuid(payload.judge.uuid));
    dispatch(updateTask(payload.task));
    dispatch(updateCurrentTask(taskKey(payload.task)));
  }
);

//selectors
const NO_LOCKS: string[] = [];
/** Athletes whose pre-selection score is locked on a sheet (stable when none). */
export const selectScoreLocks = (state: RootState, taskUuid: string): string[] =>
  state.work.scoreLocks?.[taskUuid] ?? NO_LOCKS;

export const selectTimetable = (state: RootState) => {
  return state.work.timetable;
};
/**
 * Memoized: this builds an object, so an unmemoized version would hand back a
 * fresh reference after every dispatched action. `useAppSelector` compares by
 * reference, so every consumer would re-render on every action - and any effect
 * with this value in its dependency array would re-run on every action too.
 * That is exactly how a single `adjudicatorLogIn` turned into one per dispatch.
 */
export const selectJudge = createSelector(
  [
    (state: RootState) => state.work.judgeLetter,
    (state: RootState) => state.work.judgeName,
    (state: RootState) => state.work.judgeUuid
  ],
  (letter, name, uuid) => ({ letter, name, uuid })
);
export const selectJudgesList = (state: RootState) => {
  return state.work.judgesList;
};
export const selectJudgesListSettings = (state: RootState) => {
  return state.work.judgesListSettings;
};
/** The chairperson signed in on this device, or null. */
export const selectChairperson = (state: RootState): ChairpersonSession | null =>
  state.work.chairperson ?? null;
/**
 * The chairpeople on the officials list who can sign in, i.e. who have a PIN.
 * Memoized: it builds an array, and an unmemoized one would re-render every
 * consumer on every action (see selectJudge).
 */
export const selectChairpersonLogins = createSelector(
  [(state: RootState) => state.work.judgesList],
  (list) =>
    toJudgesListEntries(list).filter(
      (official) => official?.role === 'chairperson' && Boolean(official.pinCode)
    )
);
export const selectPinLoginEnabled = (state: RootState) => {
  return state.work.judgesListSettings?.pinLoginEnabled || false;
};
export const selectCurrentTaskUuid = (state: RootState) => {
  return state.work.currentTask;
};
export const selectNextTaskUuid = (state: RootState) => {
  return state.work.nextTask;
};
export const selectCurrentTask = (state: RootState) => {
  const currentAdjudicator = state.work.judgeUuid;
  const selectedHall = state.settings.selectedHall;

  // 1. If user explicitly set a current task and it has a matching Task object, use it.
  // The pointer is a composite key (ttItemUuid || roundUuid); keep the plain
  // roundUuid match as fallback for legacy persisted pointers.
  const currentTaskUuid = state.work.currentTask;
  if (currentTaskUuid !== null && currentTaskUuid !== '') {
    const explicitTask =
      state.work.tasks.find(
        (_: Task) =>
          taskKey(_) === currentTaskUuid &&
          _.adjudicatorUuid === currentAdjudicator
      ) ||
      state.work.tasks.find(
        (_: Task) =>
          _.roundUuid === currentTaskUuid &&
          _.adjudicatorUuid === currentAdjudicator
      );
    if (explicitTask) return explicitTask;
  }

  // 2. Fall back: find active item in the hall-filtered queue
  const queue = state.work.taskManagerQueue;
  const hallFiltered = (!selectedHall || selectedHall === 'all')
    ? queue
    : queue.filter((item: ManagerItem) => (item.hall || 1) === selectedHall);

  const activeItem = hallFiltered.find((item: ManagerItem) => item.active === true);
  if (activeItem && currentAdjudicator) {
    return state.work.tasks.find(
      (_: Task) =>
        taskKey(_) === taskKey(activeItem) &&
        _.adjudicatorUuid === currentAdjudicator
    ) || null;
  }

  return null;
};

/**
 * Get all tasks for joined timetable items
 * Returns array of tasks for the same adjudicator in the join group
 */
export const selectJoinedTasks = (state: RootState) => {
  const currentTask = selectCurrentTask(state);
  const currentAdjudicator = state.work.judgeUuid;
  
  if (!currentTask || !currentTask.ttItemUuid) {
    return [];
  }
  
  // Get all ttItem UUIDs in the join group
  const joinGroupUuids: string[] = [];
  
  // Find the parent task (either current task or the one it's joined to)
  let parentTask: Task | undefined;
  let parentUuid: string;
  
  if (currentTask.isJoinedTo) {
    // Current task is a child, find the parent
    parentUuid = currentTask.isJoinedTo;
    parentTask = state.work.tasks.find(
      (t: Task) => t.ttItemUuid === parentUuid && t.adjudicatorUuid === currentAdjudicator
    );
  } else {
    // Current task is the parent or standalone
    parentUuid = currentTask.ttItemUuid;
    parentTask = currentTask;
  }
  
  // Add parent UUID
  joinGroupUuids.push(parentUuid);
  
  // Add all children UUIDs from parent's joinedWith array
  if (parentTask?.joinedWith && parentTask.joinedWith.length > 0) {
    joinGroupUuids.push(...parentTask.joinedWith);
  }

  // Get all tasks for these ttItems with same adjudicator
  // Note: Don't filter by roundUuid because joined items can have different rounds/categories
  const joinedTasks = state.work.tasks.filter(
    (task: Task) =>
      task.adjudicatorUuid === currentAdjudicator &&
      task.ttItemUuid &&
      joinGroupUuids.includes(task.ttItemUuid)
  );
  // Sort by ttItemUuid to ensure consistent order (parent first, then children)
  return joinedTasks.sort((a, b) => {
    if (a.isJoinedTo && !b.isJoinedTo) return 1;
    if (!a.isJoinedTo && b.isJoinedTo) return -1;
    return (a.ttItemUuid || '').localeCompare(b.ttItemUuid || '');
  });
};
export const selectNextTask = (state: RootState) => {
  const currentAdjudicator = state.work.judgeUuid;
  const selectedHall = state.settings.selectedHall;

  // Filter queue by hall
  const queue = state.work.taskManagerQueue;
  const hallFiltered = (!selectedHall || selectedHall === 'all')
    ? queue
    : queue.filter((item: ManagerItem) => (item.hall || 1) === selectedHall);

  // 1. Find nextActive item in the hall-filtered queue
  const nextActiveItem = hallFiltered.find(
    (item: ManagerItem) => item.nextActive === true
  );
  if (nextActiveItem && currentAdjudicator) {
    const task = state.work.tasks.find(
      (_: Task) =>
        taskKey(_) === taskKey(nextActiveItem) &&
        _.adjudicatorUuid === currentAdjudicator
    );
    if (task) return task;
  }

  // 2. Fallback: try state.work.nextTask (composite key, legacy roundUuid fallback)
  const nextTaskUuid = state.work.nextTask;
  if (nextTaskUuid !== null && nextTaskUuid !== '') {
    const task =
      state.work.tasks.find(
        (_: Task) =>
          taskKey(_) === nextTaskUuid &&
          _.adjudicatorUuid === currentAdjudicator
      ) ||
      state.work.tasks.find(
        (_: Task) =>
          _.roundUuid === nextTaskUuid &&
          _.adjudicatorUuid === currentAdjudicator
      );
    if (task) return task;
  }

  return null;
};
export const selectTaskManagerQueue = (state: RootState) => {
  return state.work.taskManagerQueue;
};
/**
 * Get task manager queue filtered by selected hall.
 * If selectedHall is 'all', returns all items.
 */
export const selectTaskManagerQueueForHall = (state: RootState) => {
  const selectedHall = state.settings.selectedHall;
  if (!selectedHall || selectedHall === 'all') {
    return state.work.taskManagerQueue;
  }
  return state.work.taskManagerQueue.filter(
    (item: ManagerItem) => (item.hall || 1) === selectedHall,
  );
};
/**
 * Get all active tasks (from any hall).
 * Adjudicators may judge in multiple halls simultaneously.
 */
export const selectActiveTasksForHall = (state: RootState): ManagerItem[] => {
  const allTasks = state.work.taskManagerQueue;
  const activeTasks = allTasks.filter(
    (item: ManagerItem) => item.active === true,
  );
  const selectedHall = state.settings.selectedHall;
  return activeTasks;
};
/**
 * Get the active task for the selected hall (single, for backward compatibility).
 */
export const selectActiveTaskForHall = (state: RootState) => {
  const selectedHall = state.settings.selectedHall;
  const activeTasks = selectActiveTasksForHall(state);
  if (!selectedHall || selectedHall === 'all') {
    return activeTasks[0] || null;
  }
  return activeTasks.find((item: ManagerItem) => (item.hall || 1) === selectedHall) || activeTasks[0] || null;
};
/**
 * Get the next active task for the selected hall.
 */
export const selectNextActiveTaskForHall = (state: RootState) => {
  const queue = selectTaskManagerQueueForHall(state);
  return queue.find((item: ManagerItem) => item.nextActive === true) || null;
};
export const selectActiveTask = (state: RootState) => {
  return state.work.taskManagerQueue.find((item: ManagerItem) => item.active);
};
export const selectTasks = (state: RootState) => {
  return state.work.tasks;
};

export const selectAllTaskResults = (state: RootState) => {
  const searchRes = state.work.tasksResults;
  return searchRes === undefined ? null : searchRes;
};

export const selectCurrentTaskResults = (state: RootState) => {
  const currentTask = selectCurrentTask(state);
  const currentAdjudicator = state.work.judgeUuid;
  if (!currentTask) return null;
  const searchRes = state.work.tasksResults?.find(
    (item: TaskResults) =>
      taskKey(item) === taskKey(currentTask) &&
      item.adjudicatorUuid === currentAdjudicator
  );
  return searchRes === undefined ? null : searchRes;
};

export const selectTaskResultsByRoundUuid = (roundUuid: string) => (state: RootState) => {
  const currentAdjudicator = state.work.judgeUuid;
  const searchRes = state.work.tasksResults?.find(
    (item: TaskResults) =>
      item.roundUuid === roundUuid &&
      item.adjudicatorUuid === currentAdjudicator
  );
  return searchRes === undefined ? null : searchRes;
};

/**
 * Task-scoped variant of {@link selectTaskResultsByRoundUuid}: matches by the
 * composite key so a round split across multiple ttItems resolves to the
 * results of exactly this task's ttItem.
 */
export const selectTaskResultsForTask =
  (task: { ttItemUuid?: string | null; roundUuid: string }) =>
  (state: RootState) => {
    const currentAdjudicator = state.work.judgeUuid;
    const searchRes = state.work.tasksResults?.find(
      (item: TaskResults) =>
        taskKey(item) === taskKey(task) &&
        item.adjudicatorUuid === currentAdjudicator
    );
    return searchRes === undefined ? null : searchRes;
  };

/**
 * Get all task results for joined tasks
 */
export const selectJoinedTasksResults = (state: RootState): TaskResults[] => {
  const joinedTasks = selectJoinedTasks(state);
  const currentAdjudicator = state.work.judgeUuid;
  
  if (!joinedTasks || joinedTasks.length === 0) {
    return [];
  }
  
  return joinedTasks
    .map((task) => {
      const results = state.work.tasksResults?.find(
        (item: TaskResults) =>
          taskKey(item) === taskKey(task) &&
          item.adjudicatorUuid === currentAdjudicator
      );
      return results;
    })
    .filter((result): result is TaskResults => result !== undefined);
};
export const selectCurrentTaskParticipantResults = (state: RootState) => {
  const currentTask = selectCurrentTask(state);
  const adjudicatorUuid = state.work.judgeUuid;
  if (state.work.participantResults[adjudicatorUuid] !== undefined) {
    // Restrict to the current task's own dances — a split round shares
    // roundUuid across ttItems but each task covers only its style's dances.
    const taskDanceUuids = new Set(
      (currentTask?.dances || []).map((d) => d.uuid)
    );
    const searchRes = state.work.participantResults[adjudicatorUuid].filter(
      (item: ParticipantResult) =>
        item.roundUuid === currentTask?.roundUuid &&
        (taskDanceUuids.size === 0 || taskDanceUuids.has(item.danceUuid))
    );
    return searchRes === undefined ? [] : searchRes;
  } else {
    return [];
  }
};
export const selectTasksCompleted = (state: RootState) => {
  return state.work.tasksCompleted;
};
export const selectSocketsCache = (state: RootState) => {
  return state.work.socketsCache;
};
export const selectLastCompletedTtUuid = (state: RootState) => {
  return state.work.lastCompletedTtUuid;
};
// Export reducer
export default workSlice.reducer;
